/**
 * The Dispatch field registry: what each field is, which entity it describes, and what a view may use it for.
 *
 * The web client and internal ops render, filter, sort, and group by this registry rather than by field name, so a
 * duty status is a set of colored pills and a timestamp gets relative-time presets without either app knowing the
 * field. Enum values mirror the store's own enumerations (the Prisma `DutyStatus` and `DrivingStatus` enums, the
 * availability values the volatile compute emits); nothing here invents a value. Presentation facts (default widths,
 * cell renderers, picker wording, swatches) live in the apps, not here. Browser-safe: no React, no DOM.
 */

import { parseFieldKey } from "@storyboard/driver-query/predicate";
import { isIntegrationTag } from "@storyboard/driver-query/tagColumns";

/**
 * The Storyboard-side fields a Dispatch view may reference as `{ strategy: "core", kind }`.
 *
 * The key source for {@link DispatchCoreKind}; {@link CORE_FIELD_KINDS} must carry one entry per kind, which a unit
 * test pins. Driver fields describe the driver a row is about; the `item_*` fields describe one message and project to
 * an empty value on a driver row. `unread` and `item_unread` are the read receipts (has anyone read it);
 * `unacknowledged` and `item_unacknowledged` are the acknowledgement layer (has a dispatcher handled it), which is
 * what the Dispatch page works from. `coordinates` is the driver's device fix as `"latitude,longitude"`, the field a
 * `withinRadius` condition targets; it is not a column a dispatcher reads, the `location` text is. `pta` and
 * `next_stop` come from the driver's current route (the planned time of availability and the first stop not yet
 * reached), blank for a driver on no route. `alert` and `unhandled_count` are reserved: they project to an empty value
 * until the TMS-events project and the unhandled-count follow-up populate them, so adding either later is a data
 * change, not a schema change.
 */
export const DISPATCH_CORE_KINDS = [
  "name",
  "alias",
  "group",
  "recent_message",
  "latest_item_at",
  "unread",
  "unacknowledged",
  "duty_status",
  "driving_status",
  "availability",
  "location",
  "coordinates",
  "state_province",
  "country",
  "volume",
  "hands_free",
  "personal_device",
  "pta",
  "next_stop",
  "item_text",
  "item_at",
  "item_sender",
  "item_unread",
  "item_unacknowledged",
  "item_kind",
  "alert",
  "unhandled_count",
] as const;
export type DispatchCoreKind = (typeof DISPATCH_CORE_KINDS)[number];

/**
 * The named colors a view may paint a column's values with, so a dispatcher can pick a column out by eye.
 *
 * Names, not hex values: the web client maps each to its palette in both themes, and a view stays readable in config.
 */
export const DISPATCH_COLORS = ["purple", "blue", "teal", "green", "amber", "orange", "red", "zinc"] as const;
export type DispatchColor = (typeof DISPATCH_COLORS)[number];

/**
 * What a row of a Dispatch view is about, and which entity a field describes.
 *
 * `driver` is the person and their channel; `message` is one item on that channel. A view's grain names the entity
 * its rows are; a message row still carries the driver's fields, since the message belongs to a driver. The store
 * calls a message an `Item`, and the stored kinds keep that spelling (`item_text`); this vocabulary is the page's.
 */
export type DispatchEntity = "driver" | "message";

/**
 * What a view may use a field for.
 *
 * `column`: laid out as a table column. `sort`: a sort key. `group`: a grouping, and with it the row menu's Only /
 * Exclude and the values editor, all of which need values that repeat across rows. `search`: a target of the free-text
 * search. A field with no roles is hidden from every picker; whether a column's header offers a filter editor, and
 * which, follows the value type instead.
 */
export type DispatchFieldRole = "column" | "sort" | "group" | "search";

/** Every role, the set an unreserved field of repeating text values takes. */
export const ALL_FIELD_ROLES: readonly DispatchFieldRole[] = ["column", "sort", "group", "search"];

/**
 * How a field's projected string should be read.
 *
 * `list` is a multi-value field such as a tag; `coordinates` is a `"latitude,longitude"` pair that a radius condition
 * measures against.
 */
export type FieldValueType = "text" | "enum" | "boolean" | "timestamp" | "number" | "list" | "coordinates";

/**
 * One value an enum field can take, with the label a dispatcher reads and the color it paints with by default.
 */
export type EnumValue = { value: string; label: string; color?: DispatchColor };

/**
 * A field's kind: what it is, whose it is, what it may do, and for enums every value the store can carry.
 */
export type FieldKind = {
  valueType: FieldValueType;
  label: string;
  /**
   * Every value the store can carry, for enum kinds and for a boolean whose two states have names of their own.
   *
   * A named boolean labels `"true"` and `"false"` (say, Unacknowledged and Acknowledged); one without names reads Yes
   * and No wherever a value is shown.
   */
  values?: readonly EnumValue[];
  /** The entity the field describes. */
  entity: DispatchEntity;
  /** What a view may use the field for; empty hides it from every picker. */
  roles: readonly DispatchFieldRole[];
  /**
   * On a message row, the message field this driver field reads as.
   *
   * So a view laid out for drivers (a Time and a Message column, an "Unacknowledged only" toggle) keeps its meaning
   * when the grain changes.
   */
  atMessageGrain?: DispatchCoreKind;
};

type Shape = Pick<FieldKind, "entity" | "roles" | "atMessageGrain">;

const driver = (roles: readonly DispatchFieldRole[], atMessageGrain?: DispatchCoreKind): Shape => ({
  entity: "driver",
  roles,
  ...(atMessageGrain ? { atMessageGrain } : {}),
});
const message = (roles: readonly DispatchFieldRole[]): Shape => ({ entity: "message", roles });
/** A field no picker offers: reserved, a backend concern, or already shown some other way. */
const HIDDEN: readonly DispatchFieldRole[] = [];

const text = (label: string, shape: Shape): FieldKind => ({ valueType: "text", label, ...shape });
const boolean = (label: string, shape: Shape, names?: { true: string; false: string }): FieldKind => ({
  valueType: "boolean",
  label,
  ...shape,
  ...(names
    ? {
        values: [
          { value: "true", label: names.true },
          { value: "false", label: names.false },
        ],
      }
    : {}),
});
const timestamp = (label: string, shape: Shape): FieldKind => ({ valueType: "timestamp", label, ...shape });
const number = (label: string, shape: Shape): FieldKind => ({ valueType: "number", label, ...shape });
const coordinates = (label: string, shape: Shape): FieldKind => ({ valueType: "coordinates", label, ...shape });
const enumeration = (label: string, values: readonly EnumValue[], shape: Shape): FieldKind => ({
  valueType: "enum",
  label,
  values,
  ...shape,
});

/** Mirrors the Prisma `DutyStatus` enum, the values `wsMembershipAvailability.mostRecentDutyStatusStatus` carries. */
export const DUTY_STATUS_VALUES: readonly EnumValue[] = [
  { value: "driving", label: "Driving", color: "green" },
  { value: "onDuty", label: "On Duty", color: "blue" },
  { value: "offDuty", label: "Off Duty", color: "zinc" },
  { value: "sleeperBed", label: "Sleeper Berth", color: "purple" },
  { value: "personalConveyance", label: "Personal Conveyance", color: "teal" },
  { value: "yardMove", label: "Yard Move", color: "amber" },
  { value: "unknown", label: "Unknown", color: "zinc" },
];

/** Mirrors the Prisma `DrivingStatus` enum. */
export const DRIVING_STATUS_VALUES: readonly EnumValue[] = [
  { value: "active", label: "Driving", color: "green" },
  { value: "inactive", label: "Not Driving", color: "zinc" },
  { value: "unknown", label: "Unknown", color: "zinc" },
];

/** The `calculatedAvailability` values the volatile compute emits. */
export const AVAILABILITY_VALUES: readonly EnumValue[] = [
  { value: "present", label: "Available", color: "green" },
  { value: "notPresent", label: "Unavailable", color: "red" },
  { value: "unknown", label: "Unknown", color: "zinc" },
];

/** The kinds of item a row can be about: a driver's message, or a TMS event the view asks its team to acknowledge. */
export const ITEM_KIND_VALUES: readonly EnumValue[] = [
  { value: "message", label: "Message", color: "blue" },
  { value: "event", label: "Event", color: "amber" },
];

const ACKNOWLEDGEMENT_NAMES = { true: "Unacknowledged", false: "Acknowledged" };

/**
 * The registry: every core kind's value type, label, entity, roles, grain alias, and enum values.
 *
 * Roles follow what a dispatcher can do with the values: text that repeats across rows may be a grouping and a
 * search target; a message body or a timestamp never repeats, so neither groups; the acknowledgement layer is never a
 * column (the red dot already says it) yet sorts, groups, and narrows. `item_unacknowledged` carries no role because
 * on a message row `unacknowledged` already means the message, and two pickers reading "Unacknowledged" would only
 * puzzle; it stays a legal control field for a view written at the message grain.
 */
export const CORE_FIELD_KINDS: Record<DispatchCoreKind, FieldKind> = {
  name: text("Name", driver(ALL_FIELD_ROLES)),
  // The alias is the channel's: the word a driver says to reach it. The team picker decides which channel every row
  // reads, so a column or condition on it would only fight that choice.
  alias: text("Channel", driver(HIDDEN)),
  // Feed groups are on their way out; the new experience never shows the first-class concept.
  group: text("Group", driver(HIDDEN)),
  recent_message: text("Message", driver(["column", "sort", "search"], "item_text")),
  latest_item_at: timestamp("Time", driver(["column", "sort"], "item_at")),
  // The row tint says it; never a field a dispatcher picks.
  unread: boolean("Unread", driver(HIDDEN, "item_unread")),
  // Named states rather than Yes and No: a group header or a chip reads "Acknowledged", not "Unacknowledged: No".
  unacknowledged: boolean("Unacknowledged", driver(["sort", "group"], "item_unacknowledged"), ACKNOWLEDGEMENT_NAMES),
  duty_status: enumeration("Duty Status", DUTY_STATUS_VALUES, driver(["column", "sort", "group"])),
  driving_status: enumeration("Driving Status", DRIVING_STATUS_VALUES, driver(["column", "sort", "group"])),
  availability: enumeration("Availability", AVAILABILITY_VALUES, driver(["column", "sort", "group"])),
  location: text("Location", driver(ALL_FIELD_ROLES)),
  // Labelled as the location too: a radius condition on the fix reads to a dispatcher as a condition on the location.
  coordinates: coordinates("Location", driver(HIDDEN)),
  state_province: text("State/Province", driver(ALL_FIELD_ROLES)),
  country: text("Country", driver(ALL_FIELD_ROLES)),
  // The projected value is the percent the driver sees, so it compares as a number rather than as a bucket.
  volume: number("Volume", driver(["column", "sort"])),
  hands_free: boolean("Hands-free", driver(["column", "sort", "group"])),
  personal_device: boolean("Personal Device", driver(["column", "sort", "group"])),
  // A projection that moves every read, so it never groups; the stop name repeats across drivers bound the same way.
  pta: timestamp("PTA", driver(["column", "sort"])),
  next_stop: text("Next Stop", driver(ALL_FIELD_ROLES)),
  item_text: text("Message", message(["column", "sort", "search"])),
  item_at: timestamp("Sent", message(["column", "sort"])),
  item_sender: text("From", message(ALL_FIELD_ROLES)),
  item_unread: boolean("Unread", message(HIDDEN)),
  item_unacknowledged: boolean("Unacknowledged", message(HIDDEN), ACKNOWLEDGEMENT_NAMES),
  item_kind: enumeration("Kind", ITEM_KIND_VALUES, message(["column", "sort", "group"])),
  alert: text("Alert", driver(HIDDEN)),
  unhandled_count: number("Unhandled", driver(HIDDEN)),
};

const coreKindSet: ReadonlySet<string> = new Set(DISPATCH_CORE_KINDS);

/**
 * Whether a string names a core kind the registry knows.
 */
export function isDispatchCoreKind(kind: string): kind is DispatchCoreKind {
  return coreKindSet.has(kind);
}

/**
 * The kind of any field a view may reference, by its key (`core:<kind>` or `tag:<slug>`).
 *
 * A core kind comes from the registry; a tag is a labelled list of the driver's with every role, except the
 * integration attribution tag, which is a backend concern and hidden. Anything else (an eld column, a key that is not
 * one) is hidden text labelled by the key, so a stored document a picker no longer offers still renders its name.
 *
 * @param field - The field key.
 * @param tagsBySlug - The workspace's tags by slug, for a tag field's label; an unknown slug labels as the slug.
 */
export function fieldKindFor(field: string, tagsBySlug: ReadonlyMap<string, { label: string }>): FieldKind {
  const ref = parseFieldKey(field);
  if (ref?.strategy === "core") {
    return isDispatchCoreKind(ref.kind) ? CORE_FIELD_KINDS[ref.kind] : text(ref.kind, driver(HIDDEN));
  }
  if (ref?.strategy === "tag") {
    return {
      valueType: "list",
      label: tagsBySlug.get(ref.slug)?.label ?? ref.slug,
      entity: "driver",
      roles: isIntegrationTag(ref.slug) ? HIDDEN : ALL_FIELD_ROLES,
    };
  }
  return text(field, driver(HIDDEN));
}

/**
 * The roles a field may play, without needing the workspace's tags.
 *
 * The registry's for a core kind, every role for a tag but the integration tag, none for anything else.
 */
export function fieldRoles(field: string): readonly DispatchFieldRole[] {
  return fieldKindFor(field, new Map()).roles;
}

/**
 * Whether a field may play a role.
 */
export function fieldHasRole(field: string, role: DispatchFieldRole): boolean {
  return fieldRoles(field).includes(role);
}

/**
 * The entity a field describes: a message for the `item_*` kinds, the driver for everything else.
 */
export function fieldEntity(field: string): DispatchEntity {
  return fieldKindFor(field, new Map()).entity;
}

/**
 * The label and default color for one value of a field, when the field is an enum that knows it.
 */
export function enumValueFor(kind: FieldKind, value: string): EnumValue | undefined {
  return kind.values?.find((candidate) => candidate.value === value);
}
